import { useState, useCallback, useRef } from "react";
import { SalaryHeadServices } from "@/services/SalaryHead/SalaryHeadServices";
import { ThrowError } from "@/api/callApi";
import { showToastNotification } from "@/utility/helper";
import { getSelectedUserIds } from "./createSalary.utils";
import type { UseMasterDataReturn } from "./useMasterData";

interface RefetchCallbacks {
  refetchEmployees: () => void;
  refetchBranchWiseUserSalary: () => void;
  refetchAttendanceSummary: () => void;
  refetchSalaryPreview: () => void;
}

export const useFileOperations = (
  masterData: UseMasterDataReturn,
  selectedEmployeeIds: Set<string | number>,
  isAllBranchUsersSelected: boolean,
  refetchCallbacks: RefetchCallbacks,
) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canDownloadTemplate =
    (isAllBranchUsersSelected || selectedEmployeeIds.size > 0) &&
    Boolean(masterData.selectedBranchId);

  const handleDownloadTemplate = useCallback(async () => {
    if (!canDownloadTemplate) {
      showToastNotification(
        "warning",
        "Please select branch users or click 'Select All Branch Users' first.",
      );
      return;
    }

    const payload = {
      branchId: masterData.selectedBranchId,
      userIds: isAllBranchUsersSelected
        ? []
        : getSelectedUserIds(selectedEmployeeIds),
    };

    setIsDownloading(true);
    try {
      const blob = await SalaryHeadServices.BranchSalaryHeadTemplate(payload);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Salary_Head_Template_${masterData.selectedBranchId}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      showToastNotification("success", "Template downloaded successfully!");
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsDownloading(false);
    }
  }, [
    canDownloadTemplate,
    masterData.selectedBranchId,
    isAllBranchUsersSelected,
    selectedEmployeeIds,
  ]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        setSelectedFile(e.target.files[0]);
      }
    },
    [],
  );

  const handleClearFile = useCallback(() => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleUploadFile = useCallback(async () => {
    if (!selectedFile) {
      showToastNotification("warning", "Please select a file to upload.");
      return;
    }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const params = masterData.selectedBranchId
        ? `branchId=${masterData.selectedBranchId}`
        : "";
      const response = await SalaryHeadServices.branchWiseSalaryImport(
        formData,
        params,
      );
      if (response?.status) {
        showToastNotification("success", "File uploaded successfully!");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        refetchCallbacks.refetchBranchWiseUserSalary();
        refetchCallbacks.refetchEmployees();
        if (masterData.activeTab === "attendance") {
          refetchCallbacks.refetchAttendanceSummary();
        } else if (masterData.activeTab === "salary") {
          refetchCallbacks.refetchSalaryPreview();
        }
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to parse import file",
        );
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsUploading(false);
    }
  }, [
    selectedFile,
    masterData.selectedBranchId,
    masterData.activeTab,
    refetchCallbacks,
  ]);

  const [showUploadConfirmModal, setShowUploadConfirmModal] =
    useState<boolean>(false);

  const handleOpenUploadConfirmModal = useCallback(() => {
    if (!selectedFile) {
      showToastNotification("warning", "Please select a file to upload.");
      return;
    }
    setShowUploadConfirmModal(true);
  }, [selectedFile]);

  const handleCloseUploadConfirmModal = useCallback(() => {
    setShowUploadConfirmModal(false);
  }, []);

  const handleConfirmUploadFile = useCallback(async () => {
    await handleUploadFile();
    setShowUploadConfirmModal(false);
  }, [handleUploadFile]);

  return {
    selectedFile,
    isDownloading,
    isUploading,
    showUploadConfirmModal,
    fileInputRef,
    canDownloadTemplate,
    handleDownloadTemplate,
    handleFileChange,
    handleClearFile,
    handleUploadFile,
    handleOpenUploadConfirmModal,
    handleCloseUploadConfirmModal,
    handleConfirmUploadFile,
  };
};

export type UseFileOperationsReturn = ReturnType<typeof useFileOperations>;
